import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import logo from '../assets/logo.svg'
import loginHero from '../assets/login-hero.svg'
import OtpInput from 'shared/components/ui/OtpInput'
import { scannerApi, apiMessage, extractData } from 'shared/api/scanner'
import { useAuth } from 'context/AuthContext'
import { getToken } from 'shared/util/storage'
import { cn } from 'shared/util/cn'

const OTP_TYPE_LOGIN = 'l'
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function detectLoginType(value) {
  const trimmed = String(value || '').trim()
  if (!trimmed) return null
  if (/[a-zA-Z@]/.test(trimmed)) return 'email'
  if (/\d/.test(trimmed)) return 'mobile'
  return null
}

function EnvelopeIcon() {
  return (
    <svg className="h-5 w-5 text-dark-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m4 7 8 6 8-6" />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg className="h-5 w-5 text-dark-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path d="M11 18h2" />
    </svg>
  )
}

const Login = () => {
  const [step, setStep] = useState('login')
  const [login, setLogin] = useState('')
  const [otp, setOtp] = useState('')
  const [otpId, setOtpId] = useState('')
  const [otpLength, setOtpLength] = useState(6)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [loading, setLoading] = useState(false)
  const [fieldError, setFieldError] = useState('')
  const navigate = useNavigate()
  const { login: setSession } = useAuth()

  useEffect(() => {
    if (getToken()) navigate('/', { replace: true })
  }, [navigate])

  useEffect(() => {
    if (secondsLeft <= 0) return undefined
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000)
    return () => clearInterval(id)
  }, [secondsLeft])

  const identifier = useMemo(() => login.trim(), [login])
  const loginType = detectLoginType(identifier)
  const isEmail = loginType === 'email'
  const isMobile = loginType === 'mobile'
  const mobileDigits = identifier.replace(/\D/g, '')
  const canSubmit = Boolean(identifier) && !loading

  const sendOtp = async (e) => {
    e?.preventDefault()
    if (!identifier) {
      setFieldError('Email or mobile number is required')
      return
    }
    if (isEmail && !EMAIL_REGEX.test(identifier)) {
      setFieldError('Enter a valid email address')
      return
    }
    if (isMobile && mobileDigits.length < 10) {
      setFieldError('Enter a valid 10-digit mobile number')
      return
    }
    if (!isEmail && !isMobile) {
      setFieldError('Enter a valid email address or mobile number')
      return
    }
    setFieldError('')
    setLoading(true)
    try {
      const { data } = await scannerApi.sendOtp({
        sLogin: isMobile ? mobileDigits : identifier,
        eUserType: 's',
        eType: isEmail ? 'e' : 'm',
        eOtpVerificationOperationType: OTP_TYPE_LOGIN,
        sCountryCode: '91'
      })
      const payload = extractData({ data })
      const id = payload?.iOTPId || payload?.iOtpId
      if (!id) {
        toast.error('OTP sent but no OTP id received. Please try again.')
        return
      }
      setOtpId(id)
      setOtp('')
      setOtpLength(payload?.nOTPLength || 6)
      setSecondsLeft(payload?.nExpireIn > 0 ? payload.nExpireIn : 60)
      setStep('otp')
      toast.success(data?.sMessage || 'OTP sent.')
    } catch (err) {
      toast.error(apiMessage(err, 'Failed to send OTP.'))
    } finally {
      setLoading(false)
    }
  }

  const verifyLock = React.useRef(false)

  const verifyOtp = async (e) => {
    e?.preventDefault()
    if (verifyLock.current) return
    if (otp.trim().length < otpLength) {
      toast.error('Enter the complete OTP.')
      return
    }
    verifyLock.current = true
    setLoading(true)
    try {
      const { data } = await scannerApi.loginOtp({ sOTP: otp.trim(), iOTPId: otpId })
      const payload = extractData({ data })
      if (!payload?.sToken) {
        toast.error('Login succeeded but no token received.')
        verifyLock.current = false
        return
      }
      setSession(payload)
      navigate('/', { replace: true })
    } catch (err) {
      toast.error(apiMessage(err, 'Invalid OTP. Please try again.'))
      setOtp('')
      verifyLock.current = false
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (otp.length === otpLength && step === 'otp' && !loading) {
      verifyOtp()
    }
  }, [otp])

  return (
    <div className="flex min-h-dvh bg-light">
      <aside className="relative hidden w-[42%] flex-col justify-between overflow-hidden bg-[#3B1818] px-10 py-12 text-light lg:flex xl:w-[46%] xl:px-16">
        <div className="flex flex-1 flex-col items-center justify-center">
          <img src={loginHero} alt="" className="w-full max-w-[380px]" />
          <div className="mt-8 h-px w-40 bg-light/25" />
        </div>
        <div className="max-w-md pb-4">
          <h2 className="mb-2 text-4xl font-bold text-light">Quick and Easy Scanning</h2>
          <p className="mb-0 text-sm text-light/80">Admit guests in seconds — just your assigned email or mobile to get started.</p>
        </div>
      </aside>

      <main className="flex flex-1 flex-col bg-light px-5 py-8 sm:px-10 lg:px-16">
        <div className="mx-auto flex w-full max-w-[420px] flex-1 flex-col">
          <div className="mb-10 flex flex-col items-center pt-4 lg:pt-8">
            <img src={logo} alt="Jordar Tickets" className="h-14 max-w-[180px] object-contain" />
          </div>

          {step === 'login'
            ? (
            <>
              <h1 className="mb-2 text-center text-5xl font-bold text-dark">Hi, Welcome Back</h1>
              <p className="mb-8 text-center text-sm text-dark-500">
                Please provide email address or mobile number to continue using our service
              </p>

              <form onSubmit={sendOtp} className="space-y-5" noValidate>
                <div>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center">
                      {isMobile ? <PhoneIcon /> : <EnvelopeIcon />}
                    </span>
                    <input
                      id="login"
                      type="text"
                      inputMode={isMobile ? 'numeric' : 'email'}
                      placeholder="Email or mobile number"
                      value={login}
                      onChange={(e) => {
                        setLogin(e.target.value)
                        if (fieldError) setFieldError('')
                      }}
                      disabled={loading}
                      autoComplete="username"
                      className={cn(
                        'h-14 w-full rounded-xl bg-dark-900 pl-12 pr-4 text-base text-dark outline-none placeholder:text-dark-600',
                        fieldError ? 'ring-1 ring-theme' : 'focus:ring-1 focus:ring-theme'
                      )}
                    />
                  </div>
                  {fieldError && <p className="mb-0 mt-2 text-xs font-medium text-theme">{fieldError}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={cn(
                    'flex h-14 w-full items-center justify-center rounded-xl text-base font-semibold',
                    canSubmit ? 'bg-theme text-light hover:bg-theme-400' : 'bg-dark-800 text-dark-400'
                  )}
                >
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </form>
            </>
              )
            : (
            <>
              <h1 className="mb-2 text-center text-5xl font-bold text-dark">Enter OTP</h1>
              <p className="mb-8 text-center text-sm text-dark-500">
                Enter the {otpLength}-digit code sent to {identifier}
              </p>
              <form onSubmit={verifyOtp} className="space-y-5">
                <OtpInput length={otpLength} value={otp} onChange={setOtp} disabled={loading} />
                <button
                  type="submit"
                  disabled={loading || otp.length < otpLength}
                  className={cn(
                    'flex h-14 w-full items-center justify-center rounded-xl text-base font-semibold',
                    otp.length >= otpLength && !loading ? 'bg-theme text-light hover:bg-theme-400' : 'bg-dark-800 text-dark-400'
                  )}
                >
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </button>
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    className="font-semibold text-theme"
                    onClick={() => {
                      setStep('login')
                      setOtp('')
                      setOtpId('')
                    }}
                    disabled={loading}
                  >
                    Change {isEmail ? 'email' : 'number'}
                  </button>
                  <button
                    type="button"
                    className="font-semibold text-theme disabled:text-dark-600"
                    onClick={sendOtp}
                    disabled={loading || secondsLeft > 0}
                  >
                    {secondsLeft > 0 ? `Resend in ${secondsLeft}s` : 'Resend OTP'}
                  </button>
                </div>
              </form>
            </>
              )}

          <p className="mt-auto pt-10 text-center text-sm text-dark-500">
            Need access? <span className="font-semibold text-theme">Contact your organiser</span>
          </p>
        </div>
      </main>
    </div>
  )
}

export default Login
